<script lang="ts">
    import { Component, mixins } from 'vue-facing-decorator';
    import { insertIntoTextArea1, localToUtcDate } from "@common/Common";
    import AttributesQueries from "@Alternatives/Attributes/Domain/queries-vue-component";
    import AttributesCommands from "@Alternatives/Attributes/Domain/commands-vue-component";
    import { CreateDateAttributeCommandV1, CreateEmailAttributeCommandV1, CreateMultilineAttributeCommandV1, CreateNumericAttributeCommandV1, CreateTextAttributeCommandV1, UpdateDateAttributeCommandV1, UpdateEmailAttributeCommandV1, UpdateMultilineAttributeCommandV1, UpdateNumericAttributeCommandV1, UpdateTextAttributeCommandV1 } from '@Alternatives/Attributes/Domain/commands-core';
    import { AttributeDefinedValueReadModel, CalcScriptResultReadModel, EmailAttributeDefinedValuesFromPeopleTagReadModel } from '@Alternatives/Attributes/Domain/readmodels';
    import { ValidationError } from '@common/types';
    import { DatePicker, DatePickerChangeEvent } from '@progress/kendo-vue-dateinputs';
    import { RealDefinedValue } from '@Alternatives/Model';
    import { ICommandBase } from '@Alternatives/cqrs';
    import { ICommandStatus } from '@cqrs/cqrs';
    import { AttributeEditorDialogViewModel } from '@Alternatives/Attributes/Components/AttributeEditorDialog';


    @Component({
        components: { 
            DatePicker 
        },
        props: {
            viewModel: { type: Object, required: true }
        },
        watch: {
            testScriptPanelVisible: 'onTestScriptPanelVisibleChanged'
        }
    })
    export default class AttributeEditorDialog extends mixins(AttributesQueries, AttributesCommands)
    {
        get vm(): AttributeEditorDialogViewModel { return this.$props.viewModel as AttributeEditorDialogViewModel; }

        get opened(): boolean { return this.vm?.opened ?? false; }
        get initialized(): boolean { return this.vm.initialized; }

        get workspaceId(): number { return this.vm.workspaceId; }
        get projectId(): number { return this.vm.projectId; }
        get alternativesListId(): number { return this.vm.alternativesListId; }

        get dialogTitle(): string { return this.vm.dialogTitle; }
        get confirmButtonLabel(): string { return this.vm.confirmButtonLabel; }
        get allowChangeAttributeType(): boolean { return this.vm.allowChangeAttributeType; }
        get allowMultipleValuesEnabled(): boolean { return this.vm.allowMultipleValuesEnabled; }
        get allowInputTypeEvaluate(): boolean { return this.vm.allowInputTypeEvaluate; }

        get scriptTestInputs(): { Name: string, Value: number }[] { return this.vm.scriptTestInputs; }
        get scales(): { Id: number, Name: string, Levels: { Name: string, Value: number, Description: string }[] }[] { return this.vm.scales; }
        get peopleTags(): string[] { return this.vm.peopleTags; }
        get numericAttributeNames(): string[] { return this.vm.numericAttributeNames; }

        get scriptTestResult(): string | null { return this.vm.scriptTestResult; }
        set scriptTestResult(val: string | null) { this.vm.scriptTestResult = val; }

        get nameError(): string | null { return this.vm.errors.find(e => e.Context === "Name")?.Error ?? null; }
        get descriptionError(): string | null { return this.vm.errors.find(e => e.Context === "Description")?.Error ?? null; }
        get sectionError(): string | null { return this.vm.errors.find(e => e.Context === "Section")?.Error ?? null; }
        get scriptError(): string | null { return this.vm.errors.find(e => e.Context === "Script")?.Error ?? null; }
        get definedValuesErrors(): string[] { return this.vm.errors.filter(e => e.Context === "DefinedValues").map(e => e.Error); }


        get id(): number { return this.vm.attributeId; }

        get name(): string { return this.vm.name; }
        set name(val: string) { this.vm.name = val; }

        get section(): string | null { return this.vm.section; }
        set section(val: string | null) { this.vm.section = val; }

        get description(): string | null { return this.vm.description; }
        set description(val: string | null) { this.vm.description = val; }

        get script(): string | null { return this.vm.script; }
        set script(val: string | null) { this.vm.script = val; }

        get attributeType(): string { return this.vm.attributeType; }
        set attributeType(val: string) { this.vm.attributeType = val; }

        get inputType(): string { return this.vm.inputType; }
        set inputType(val: string) { this.vm.inputType = val; }

        get isMultivalue(): boolean { return this.vm.isMultivalue; }
        set isMultivalue(val: boolean) { this.vm.isMultivalue = val; }

        get minLength(): number | null { return this.vm.minLength; }
        set minLength(val: number | null) { this.vm.minLength = val; }

        get maxLength(): number | null { return this.vm.maxLength; }
        set maxLength(val: number | null) { this.vm.maxLength = val; }

        get minValue(): number | null { return this.vm.minValue; }
        set minValue(val: number | null) { this.vm.minValue = val; }

        get maxValue(): number | null { return this.vm.maxValue; }
        set maxValue(val: number | null) { this.vm.maxValue = val; }

        get minDate(): Date | null { return this.vm.minDate; }
        set minDate(val: Date | null) { this.vm.minDate = val; }

        get maxDate(): Date | null { return this.vm.maxDate; }
        set maxDate(val: Date | null) { this.vm.maxDate = val; }

        get rows(): number | null { return this.vm.rows; }
        set rows(val: number | null) { this.vm.rows = val; }

        get definedValues(): AttributeDefinedValueReadModel[] { return this.vm.definedValues; }
        set definedValues(val: AttributeDefinedValueReadModel[]) { this.vm.definedValues = val; }

        testScriptPanelVisible: boolean = false;
        
        //watcher: testScriptPanelVisible
        onTestScriptPanelVisibleChanged(val: boolean, oldVal: boolean)
        {
            if (val === true) {
                const script = this.script;
                const re = /\[([a-zA-Z0-9_]{1,100})\]/g; // valid names with up to 100 characters

                const matches: Set<string> = new Set<string>();
                let match;
                let matchCount = 0;

                while ((match = re.exec(script)) !== null) {
                    matches.add(match[1]);
                    matchCount++;
                    if (matchCount >= 100) break; // Stop after 100 matches
                }

                const newValues = Array.from(matches).map(a => {
                    const vals = this.scriptTestInputs.filter(b => b.Name === a);
                    return { Name: a, Value: vals.length > 0 ? vals[0].Value : 0 };
                });

                this.vm.scriptTestInputs = newValues;
            }
        }
        
        
        addDefinedValue()
        {
            this.definedValues.push({ Name: "", Value: null, Description: null });
        }

        removeDefinedValue(dv: AttributeDefinedValueReadModel)
        {
            this.definedValues.remove(dv);
        }

        insertAttributeIntoScript(attrName: string)
        {
            insertIntoTextArea1(this.$refs.ta as HTMLTextAreaElement, `[${attrName}]`);
            (this.$refs.ta as any).dispatchEvent(new Event('input'));
        }


        onMinDateChanged(event: DatePickerChangeEvent)
        {
            const localDate = event.target.value as Date;
            this.minDate = localToUtcDate(localDate);
        }

        onMaxDateChanged(event: DatePickerChangeEvent)
        {
            const localDate = event.target.value as Date;
            this.maxDate = localToUtcDate(localDate);
        }

        onDefinedLevelDateChanged(event: DatePickerChangeEvent, dv: AttributeDefinedValueReadModel)
        {
            const localDate = event.target.value as Date;
            dv.Value = localToUtcDate(localDate);
        }


        handleLoadEmailDefinedValuesByPeopleTag(tag: string)
        {
            this.runEmailAttributeDefinedValuesByPeopleTagQueryV1(this.workspaceId, this.projectId, tag);
        }

        handleLoadScriptTestResult()
        {
            this.scriptTestResult = null;
            this.runCalcAttributeScriptTestQueryV1(this.workspaceId, this.projectId, this.alternativesListId, this.script, this.scriptTestInputs);
        }

        handleFormSubmit()
        {
            this.vm.clearValidationErrors();            

            if (this.vm.attributeId)
            {
                const definedValues = this.definedValues.filter(a => a.Name || a.Value || a.Description);

                switch (this.attributeType)
                {
                    case "Real": {
                        this.$command(new UpdateNumericAttributeCommandV1(this.workspaceId, this.projectId, this.alternativesListId, this.id,
                            this.name, this.description, this.section, this.inputType, this.isMultivalue, this.minValue, this.maxValue, this.script, definedValues as RealDefinedValue[]), this.id.toString());
                        break;
                    }

                    case "Date": {
                        this.$command(new UpdateDateAttributeCommandV1(this.workspaceId, this.projectId, this.alternativesListId, this.id,
                            this.name, this.description, this.section, this.inputType, this.isMultivalue, this.minDate, this.maxDate, definedValues), this.id.toString());
                        break;
                    }

                    case "Text": {
                        this.$command(new UpdateTextAttributeCommandV1(this.workspaceId, this.projectId, this.alternativesListId, this.id,
                            this.name, this.description, this.section, this.inputType, this.isMultivalue, this.minLength, this.maxLength, definedValues), this.id.toString());
                        break;
                    }

                    case "Email": {
                        this.$command(new UpdateEmailAttributeCommandV1(this.workspaceId, this.projectId, this.alternativesListId, this.id,
                            this.name, this.description, this.section, this.inputType, this.isMultivalue, definedValues), this.id.toString());
                        break;
                    }

                    case "MultilineText": {
                        this.$command(new UpdateMultilineAttributeCommandV1(this.workspaceId, this.projectId, this.alternativesListId, this.id,
                            this.name, this.description, this.section, this.rows ?? 3, this.minLength, this.maxLength), this.id.toString());
                        break;
                    }

                    default:
                        console.log(`Unknown attribute type: ${this.attributeType}`);
                        break;
                }
            }
            else
            {
                const definedValues = this.definedValues.filter(a => a.Name || a.Value || a.Description);
                const internalName = this.vm.internalName ?? null;

                switch (this.attributeType)
                {
                    case "Real":
                        this.invokeCreateNumericAttributeCommandV1(false, this.workspaceId, this.projectId, this.alternativesListId, 0, internalName, this.name, this.description, this.section, this.inputType, this.isMultivalue, this.minValue, this.maxValue, this.script, definedValues as RealDefinedValue[]);
                        break;

                    case "Date":
                        this.invokeCreateDateAttributeCommandV1(false, this.workspaceId, this.projectId, this.alternativesListId, 0, internalName, this.name, this.description, this.section, this.inputType, this.isMultivalue, this.minDate, this.maxDate, definedValues);
                        break;

                    case "Text":
                        this.invokeCreateTextAttributeCommandV1(false, this.workspaceId, this.projectId, this.alternativesListId, 0, internalName, this.name, this.description, this.section, this.inputType, this.isMultivalue, this.minLength, this.maxLength, definedValues);
                        break;

                    case "Email":
                        this.invokeCreateEmailAttributeCommandV1(false, this.workspaceId, this.projectId, this.alternativesListId, 0, internalName, this.name, this.description, this.section, this.inputType, this.isMultivalue, definedValues);
                        break;

                    case "MultilineText": {
                        this.invokeCreateMultilineAttributeCommandV1(false, this.workspaceId, this.projectId, this.alternativesListId, 0, internalName, this.name, this.description, this.section, this.rows ?? 3, this.minLength, this.maxLength);
                        break;
                    }

                    default:
                        console.log(`Unknown attribute type: ${this.attributeType}`);
                        break;
                }
            }
        }
        
        onSetModel(model: any, modelName: string) 
        {
            if (modelName === "CalcScriptResultReadModel")
            {
                this.vm.setScriptTestResult(model as CalcScriptResultReadModel);
            }
            else if (modelName === "EmailAttributeDefinedValuesFromPeopleTagReadModel")
            {
                this.vm.setPeople(model as EmailAttributeDefinedValuesFromPeopleTagReadModel);
            }
        }


        onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
        {
            if (CreateNumericAttributeCommandV1.matchType(cmd))        
            {
                const attrId = Number(result);
                this.$emit("numeric-attribute-created", attrId, this.maxValue);
                this.$emit("attribute-created", attrId);
                this.close();
            }
            else if (CreateDateAttributeCommandV1.matchType(cmd) ||
                CreateTextAttributeCommandV1.matchType(cmd) ||
                CreateMultilineAttributeCommandV1.matchType(cmd) ||
                CreateEmailAttributeCommandV1.matchType(cmd))        
            {
                const attrId = Number(result);                
                this.$emit("attribute-created", attrId);
                this.close();
            }
            else if (UpdateNumericAttributeCommandV1.matchType(cmd))  
            {
                const attrId = Number.parseInt(correlationId);
                this.$emit("attribute-updated", attrId);
                this.$emit("numeric-attribute-updated", attrId);
                this.close();
            }
            else if (UpdateDateAttributeCommandV1.matchType(cmd) ||
                UpdateTextAttributeCommandV1.matchType(cmd) ||
                UpdateEmailAttributeCommandV1.matchType(cmd) ||
                UpdateMultilineAttributeCommandV1.matchType(cmd))  
            {
                const attrId = Number.parseInt(correlationId);        
                this.$emit("attribute-updated", attrId);                
                this.close();
            }
        }

        onCommandFailed(status: ICommandStatus)
        {
            if (CreateNumericAttributeCommandV1.matchType(status.Command) ||
                CreateDateAttributeCommandV1.matchType(status.Command) ||
                CreateTextAttributeCommandV1.matchType(status.Command) ||
                CreateMultilineAttributeCommandV1.matchType(status.Command) ||
                CreateEmailAttributeCommandV1.matchType(status.Command))        
            {
                this.vm.displayValidationErrors(status.ValidationErrors);
            }
            else if (UpdateNumericAttributeCommandV1.matchType(status.Command) ||
                UpdateDateAttributeCommandV1.matchType(status.Command) ||
                UpdateTextAttributeCommandV1.matchType(status.Command) ||
                UpdateMultilineAttributeCommandV1.matchType(status.Command) ||
                UpdateEmailAttributeCommandV1.matchType(status.Command))
            {
                this.vm.displayValidationErrors(status.ValidationErrors);
            }
        }


        close()
        {
            this.vm.close();
        }
    }

</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{dialogTitle}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>

                        <div v-if="initialized" class="container-fluid">
                            <form id="attribute-editor-dialog-form" @submit.prevent="handleFormSubmit">
                                <div class="row">
                                    <div class="col-sm-6">
                                        <div class="form-group">
                                            <label><span class="text-danger">*</span> Name</label>
                                            <input v-model.trim="name" type="text" class="form-control" required minlength="2" maxlength="100" />
                                            <small>Name of the attribute</small>
                                            <div class="error">{{nameError}}</div>
                                        </div>
                                    </div>

                                    <div class="col-sm-6">
                                        <div class="form-group">
                                            <label>Section</label>
                                            <input type="text" v-model.trim="section" class="form-control" maxlength="100" />
                                            <small>Name of the section. Use sections to group attributes e.g. financials.</small>
                                            <div class="error">{{sectionError}}</div>
                                        </div>
                                    </div>

                                    <div class="col-sm-12">
                                        <div class="form-group">
                                            <label>Description</label>
                                            <textarea v-model.trim="description" type="text" class="form-control" rows="3" maxlength="500"></textarea>
                                            <small>Description of the attribute.</small>
                                            <div class="error">{{descriptionError}}</div>
                                        </div>
                                    </div>
                                </div>
                                <hr />

                                <div class="row">
                                    <div class="col-sm-4">
                                        <div class="form-group">
                                            <label>Type</label>
                                            <select class="form-control" v-model="attributeType" :disabled="!allowChangeAttributeType" required>
                                                <option value="Real">Numeric</option>
                                                <option value="Text">Text</option>
                                                <option value="MultilineText">Multiline text</option>
                                                <option value="Date">Date</option>
                                                <option value="Email">Email</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div class="col-sm-4" v-if="attributeType === 'Real'">
                                        <div class="form-group">
                                            <label>Input type</label>
                                            <select class="form-control" v-model="inputType" required>
                                                <option value="Manual">Enter value manually</option>
                                                <option value="List">Pick value from list</option>
                                                <option value="Evaluate" :disabled="allowInputTypeEvaluate === false">
                                                    Based on formula
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    <template v-if="attributeType !== 'MultilineText'">
                                        <div class="col-sm-4" v-if="attributeType !== 'Real'">
                                            <div class="form-group">
                                                <label>Input type</label>
                                                <select class="form-control" v-model="inputType">
                                                    <option value="Manual">Enter value manually</option>
                                                    <option value="List">Pick value from list</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div class="col-sm-4" v-if="allowMultipleValuesEnabled === true && inputType !== 'Evaluate'">
                                            <div class="form-group">
                                                <label>Multi-select</label>
                                                <div class="checkbox">
                                                    <label>
                                                        <input v-model="isMultivalue" type="checkbox"> Allow multiple values
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                                <br />

                                <template v-if="inputType === 'Manual'">
                                    <div v-if="attributeType === 'Text'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min length</label>
                                            <input v-model.number="minLength" type="number" class="form-control" min="0" step="1" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max length</label>
                                            <input v-model.number="maxLength" type="number" class="form-control" min="1" step="1" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'MultilineText'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min length</label>
                                            <input v-model.number="minLength" type="number" class="form-control" min="0" step="1" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max length</label>
                                            <input v-model.number="maxLength" type="number" class="form-control" min="1" step="1">
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Number of lines</label>
                                            <input v-model.number="rows" type="number" class="form-control" min="0" step="1" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'Real'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min value</label>
                                            <input v-model.number="minValue" type="number" class="form-control" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max value</label>
                                            <input v-model.number="maxValue" type="number" class="form-control" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'Date'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min value</label>
                                            <DatePicker :value="minDate" @change="onMinDateChanged" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max value</label>
                                            <DatePicker :value="maxDate" @change="onMaxDateChanged" />
                                        </div>
                                    </div>

                                </template>

                                <template v-if="inputType === 'List'">
                                    <div class="mt-3">
                                        <button class="btn btn-sm btn-outline-secondary mr-1" @click="addDefinedValue">Add value</button>

                                        <div v-if="attributeType === 'Real'" class="dropdown d-inline">
                                            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                                Scales templates
                                            </button>

                                            <div class="dropdown-menu" style="max-height: 300px; overflow-y: auto;">
                                                <a v-for="s in scales" :key="s.Id" class="dropdown-item" @click="definedValues = s.Levels">
                                                    {{s.Name}}
                                                </a>
                                            </div>
                                        </div>

                                        <div v-if="attributeType === 'Email'" class="dropdown d-inline">
                                            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                                Add from People
                                            </button>

                                            <div class="dropdown-menu" style="max-height: 300px; overflow-y: auto;">
                                                <a class="dropdown-item" @click="handleLoadEmailDefinedValuesByPeopleTag(null)">All</a>
                                                <div class="dropdown-divider"></div>
                                                <a class="dropdown-item" v-for="t in peopleTags" :key="t" @click="handleLoadEmailDefinedValuesByPeopleTag(t)">{{t}}</a>
                                            </div>
                                        </div>

                                    </div>

                                    <div class="row mt-3">
                                        <div class="col-sm-3">
                                            <label>Name</label>
                                        </div>
                                        <div class="col-sm-4">
                                            <label>
                                                {{ attributeType !== 'Email' ? 'Value' : 'Email'  }}
                                            </label>
                                        </div>
                                        <div class="col-sm-4">
                                            <label>Description</label>
                                        </div>
                                    </div>

                                    <div v-for="dv in definedValues">
                                        <div class="row" style="margin-top: 5px;">
                                            <div class="col-sm-3">
                                                <input type="text" v-model.trim="dv.Name" class="form-control" required minlength="2" maxlength="100" />
                                            </div>
                                            <div class="col-sm-4">
                                                <div v-if="attributeType === 'Text'">
                                                    <input type="text" v-model.trim="dv.Value" required class="form-control" />
                                                </div>

                                                <div v-if="attributeType === 'Real'">
                                                    <input type="number" v-model.number="dv.Value" required class="form-control" />
                                                </div>

                                                <div v-if="attributeType === 'Date'">
                                                    <DatePicker :value="dv.Value != null ? new Date(dv.Value) : null" @change="onDefinedLevelDateChanged($event, dv)" />
                                                </div>

                                                <div v-if="attributeType === 'Email'">
                                                    <input type="email" v-model.trim="dv.Value" required class="form-control" />
                                                </div>
                                            </div>

                                            <div class="col-sm-4">
                                                <textarea type="text" v-model.trim="dv.Description" class="form-control" rows="2"></textarea>
                                            </div>

                                            <div class="col-sm-1">
                                                <button type="button" @click="removeDefinedValue(dv)" class="btn btn-outline-secondary">
                                                    <i class="fa fa-times-circle"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-for="e in definedValuesErrors">
                                        <div class="error">{{e}}</div>
                                    </div>

                                </template>

                                <template v-if="inputType === 'Evaluate'">
                                    <label>Formula</label>
                                    <div class="d-flex justify-content-end">
                                        <div class="dropdown">
                                            <button class="btn btn-sm btn-secondary dropdown-toggle" data-toggle="dropdown">Insert</button>
                                            <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                <a v-for="attrName in numericAttributeNames" :key="attrName" class="dropdown-item" @click="insertAttributeIntoScript(attrName)">
                                                    {{attrName}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <textarea ref="ta" v-model="script" rows="5" class="form-control"></textarea>
                                        <div class="error">{{scriptError}}</div>
                                    </div>

                                    <a class="text-primary" @click="testScriptPanelVisible = !testScriptPanelVisible">Test formula</a>
                                    <br />
                                    <div v-if="testScriptPanelVisible === true">
                                        <div class="shadow-none p-3 mb-3 bg-light rounded">
                                            <div v-for="i in scriptTestInputs" class="form-inline">
                                                <label>{{i.Name}}: </label>
                                                <input type="number" v-model.number="i.Value" class="form-control" style="width: 90px;" />
                                            </div>
                                            <br />

                                            <div class="row">
                                                <div class="col-sm-6">
                                                    <button type="button" @click="handleLoadScriptTestResult" class="btn btn-outline-secondary">Test</button>
                                                </div>

                                                <div class="col-sm-6">
                                                    <h3 style="margin: 0;">
                                                        Result: {{scriptTestResult}}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </form>

                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="submit" form="attribute-editor-dialog-form" :disabled="!initialized" class="btn btn-primary">{{confirmButtonLabel}}</button>
                        <button type="button" @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
    .k-calendar-container {
        z-index: 10000 !important;
    }
</style>